import { Box, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { ActionComponentProps } from '../../index';
import { CollapsibleOptions } from './CollapsibleOptions';

export const FinishPickUp = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces] = useUpdatePiecesMutation();

  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);

  const confirmAction = async () => {
    try {
      setActionInProgress(true);

      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishPickUp,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      setActionInProgress(false);
      onActionFinish();
    } catch (error) {
      setActionInProgress(false);
    }
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {mailpieces.length === 1 ? (
          <>Please give this item to the recipient, and click Complete when you&apos;re done.</>
        ) : (
          <>
            Please give <strong>{mailpieces.length}</strong> items to the recipient, and click Complete when you&apos;re
            done.
          </>
        )}
      </Typography>

      <CollapsibleOptions>
        <TextField
          sx={{ width: '100%', mt: 1, mb: 2 }}
          label="Optional Comment"
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <NotifyMailroomSwitch
          checked={notifyMailroom}
          onChange={() => setNotifyMailroom(!notifyMailroom)}
          label="Notify mailroom users about comment"
        />
        {isAdmin && (
          <Box sx={{ mt: 1 }}>
            <NotifyMailroomSwitch
              checked={skipNotifyRecipient}
              onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
              label="Skip notification to recipient about completed request"
            />
          </Box>
        )}
      </CollapsibleOptions>
    </Box>
  );

  return (
    <DialogConfirmation
      open={actionStarted}
      loading={actionInProgress}
      maxWidth="sm"
      title={mailpieces.length === 1 ? 'Complete pickup' : 'Complete pickup for selected items'}
      content={content}
      acceptButtonHandleClick={confirmAction}
      cancelButtonHandleClick={onActionFinish}
      acceptButtonTitle="Complete"
      cancelButtonTitle="Cancel"
    />
  );
};
